import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, X, Star, Trash2, Menu, MapPin, ShieldCheck, CheckCircle, Lock, Heart, UserMinus } from 'lucide-react';

// --- CONSTANTS ---
const GOOGLE_MAPS_API_KEY = 'AIzaSyAJQZLB6xzkXehtcMOFcQlcS0P-PGvxSt4';
const STRIPE_PUBLIC_KEY = 'pk_live_51SWUy2E3fQj5OVlF6IVoZSwahyyj3IRORR0M5OMO2N87OYRxNqikjfapGmBSEdvcPqR24u55qM3N04YePjVR4J5q00m062wmSP';

// API URLs
const FUNCTION_BASE = 'https://us-central1-rubber-band-man-creations.cloudfunctions.net';
const API_URL = `${FUNCTION_BASE}/createPaymentIntent`;
const SUB_URL = `${FUNCTION_BASE}/createSubscription`;
const CANCEL_URL = `${FUNCTION_BASE}/cancelSubscription`;
const EMAIL_API_URL = `${FUNCTION_BASE}/sendOrderEmail`; 

// --- Components ---

// 1. Payment Section - Using Vanilla Stripe (No Imports)
const PaymentSection = ({ total, onPaymentSuccess, shippingInfo, cart, isSubscription, subscriptionColors }) => {
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const paymentElementRef = useRef(null);
  const initialized = useRef(false);

  // 1. Load Stripe.js & Fetch Secret
  useEffect(() => {
    // Determine endpoint and payload
    const endpoint = isSubscription ? SUB_URL : API_URL;
    const payload = isSubscription 
        ? { email: shippingInfo.email, name: shippingInfo.name, colors: subscriptionColors.join(",") } 
        : { amount: total, email: shippingInfo.email, name: shippingInfo.name, items: cart };

    // Fetch Secret
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Backend Error: ${errorText}`);
        }
        return res.json();
      })
      .then((data) => setClientSecret(data.clientSecret))
      .catch((err) => {
          console.error("Error fetching payment intent:", err);
          setErrorMessage("Could not connect to payment server.");
      });

    // Load Stripe Script Dynamically (No npm install needed)
    if (!window.Stripe) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        if (window.Stripe) {
          setStripe(window.Stripe(STRIPE_PUBLIC_KEY));
        }
      };
      document.body.appendChild(script);
    } else {
      setStripe(window.Stripe(STRIPE_PUBLIC_KEY));
    }
  }, [total, isSubscription, shippingInfo, subscriptionColors, cart]);

  // 2. Initialize Payment Element (Vanilla JS style)
  useEffect(() => {
    if (stripe && clientSecret && paymentElementRef.current && !initialized.current) {
      const appearance = {
        theme: 'stripe',
        variables: {
          colorPrimary: '#F97316',
          fontFamily: '"Roboto", sans-serif',
        },
      };
      
      // Create Stripe Elements instance
      const elems = stripe.elements({ clientSecret, appearance });
      
      // Create and Mount the Payment Element to the DIV
      const paymentElement = elems.create('payment', {
        layout: 'tabs'
      });
      paymentElement.mount(paymentElementRef.current);
      
      setElements(elems);
      initialized.current = true;
    }
  }, [stripe, clientSecret]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return; 
    }

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href, 
        payment_method_data: {
            billing_details: {
                name: shippingInfo.name,
                email: shippingInfo.email,
                address: {
                    line1: shippingInfo.address,
                    city: shippingInfo.city,
                    state: shippingInfo.state,
                    postal_code: shippingInfo.zip,
                    country: 'US', 
                }
            }
        }
      },
      redirect: "if_required", 
    });

    if (error) {
      setErrorMessage(error.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // --- SUCCESS ---
      const orderId = paymentIntent.id.slice(-6).toUpperCase();
      
      // Send Email
      fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            orderId: orderId,
            customerName: shippingInfo.name,
            customerEmail: shippingInfo.email,
            shippingAddress: shippingInfo,
            items: isSubscription ? [] : cart,
            total: total,
            isSubscription: isSubscription,
            subscriptionColors: isSubscription ? subscriptionColors.join(", ") : ""
        })
      }).catch(err => console.error("Email send failed", err));

      localStorage.removeItem('pendingOrder');
      onPaymentSuccess(paymentIntent);
      setIsProcessing(false);
    } else {
      setErrorMessage("Payment status: " + (paymentIntent?.status || "Unknown"));
      setIsProcessing(false);
    }
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-700 flex items-center">
            <ShieldCheck className="w-5 h-5 mr-2 text-orange-500"/> Secure Payment
          </h4>
          {isSubscription && (
             <span className="bg-orange-100 text-orange-800 text-xs font-bold px-2 py-1 rounded-full">Monthly Plan</span>
          )}
        </div>
        
        {/* Loading Spinner */}
        {(!clientSecret || !stripe) && (
          <div className="flex justify-center py-8">
             <svg className="animate-spin h-8 w-8 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}

        {/* Stripe Mount Point */}
        <div ref={paymentElementRef} id="payment-element"></div>
      </div>

      {errorMessage && (
        <div className="text-red-500 text-sm font-bold bg-red-50 p-3 rounded-md border border-red-100">
          {errorMessage}
        </div>
      )}

      <button 
        onClick={handleSubmit}
        disabled={!stripe || !elements || isProcessing}
        className="w-full bg-black text-white px-6 py-4 rounded-xl font-bold text-lg border-2 border-black shadow-[3px_3px_0px_rgba(150,150,150,1)] hover:bg-green-600 hover:border-green-800 hover:shadow-none transition-all active:translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isProcessing ? (
          <>
             <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : (
          `PAY $${total}`
        )}
      </button>
      
      <div className="text-center mt-2">
        <p className="text-xs text-gray-400 flex items-center justify-center">
          <Lock className="w-3 h-3 mr-1" /> Encrypted by Stripe
        </p>
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS ---

const BraceletIcon = ({ color1, color2, color3, color4, color5, color6, pattern }) => {
  const circumference = 2 * Math.PI * 40; 
  const segmentCount = 12; 
  const segmentLength = circumference / segmentCount;
  const dashSize = segmentLength - 4; 
  const colors = [color1, color2, color3, color4, color5, color6].filter(Boolean);
  const numColors = colors.length;
  const dashArray = `${dashSize} ${segmentLength * numColors - dashSize}`; 

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg transform -rotate-12 group-hover:rotate-0 transition-all duration-500">
      {/* Shadow Layer */}
      <circle cx="52" cy="52" r="40" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="14" />
      {colors.map((color, index) => (
        <circle 
            key={index} cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="14" 
            strokeDasharray={dashArray} strokeDashoffset={`-${segmentLength * index}`} strokeLinecap="round" 
        />
      ))}
      <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" strokeDasharray="4 8" />
    </svg>
  );
};

const ProductCard = ({ product, addToCart }) => {
  const [size, setSize] = useState('Kids');
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-xl transform transition hover:scale-105 hover:rotate-1 border-4 border-black group flex flex-col h-full">
      <div className="h-48 bg-gray-100 p-6 relative overflow-hidden flex-shrink-0">
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50"></div>
        <div className="w-full h-full transform group-hover:scale-110 transition duration-500">
          <BraceletIcon color1={product.c1} color2={product.c2} color3={product.c3} color4={product.c4} color5={product.c5} color6={product.c6} pattern={product.pattern} />
        </div>
        {product.badge && (
          <div className="absolute top-2 right-2 bg-yellow-400 text-black font-black px-3 py-1 text-xs uppercase -rotate-6 border-2 border-black shadow-[2px_2px_0px_rgba(0,0,0,1)] z-10">
            {product.badge}
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-2">
            <h3 className="font-['Bangers'] text-2xl tracking-wide text-gray-900 leading-none">{product.name}</h3>
            <p className="text-sm text-gray-600 font-medium">{product.desc}</p>
        </div>
        <div className="mt-auto pt-4">
            <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Select Size:</label>
            <div className="flex space-x-2 mb-4">
                {['Kids', 'Adults/Kids at Heart'].map(s => (
                    <button key={s} onClick={() => setSize(s)} className={`flex-1 py-1 px-2 text-xs font-bold border-2 rounded-md transition-all ${size === s ? 'bg-orange-500 border-black text-white shadow-[2px_2px_0px_rgba(0,0,0,1)]' : 'bg-white border-gray-200 text-gray-500 hover:border-orange-300'}`}>
                        {s}
                    </button>
                ))}
            </div>
            <div className="flex justify-between items-center">
            <span className="text-xl font-black text-orange-600">${product.price.toFixed(2)}</span>
            <button onClick={() => addToCart({ ...product, selectedSize: size })} className="bg-black text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-500 hover:text-black transition-colors border-2 border-black shadow-[3px_3px_0px_rgba(150,150,150,1)] active:translate-y-1 active:shadow-none">ADD TO BAG</button>
            </div>
        </div>
      </div>
    </div>
  );
};

const ShippingForm = ({ onSubmit, onBack, initialData }) => {
    const [formData, setFormData] = useState(initialData || { name: '', email: '', address: '', city: '', state: '', zip: '' });
    // Removed unused refs
    // const addressInputRef = useRef(null);
    // const autoCompleteRef = useRef(null);

    useEffect(() => {
        const initAutocomplete = () => {
            const inputElement = document.getElementById('shipping-address-input');
            if (!inputElement || !window.google || !window.google.maps || !window.google.maps.places) return;
            try {
                const autocomplete = new window.google.maps.places.Autocomplete(inputElement, { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['address_components', 'formatted_address'] });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (!place.address_components) return;
                    let streetNumber = '', route = '', city = '', state = '', postcode = '';
                    for (const component of place.address_components) {
                        const componentType = component.types[0];
                        if(componentType === 'street_number') streetNumber = component.long_name;
                        if(componentType === 'route') route = component.short_name;
                        if(componentType === 'postal_code') postcode = component.long_name;
                        if(componentType === 'locality') city = component.long_name;
                        if(componentType === 'administrative_area_level_1') state = component.short_name;
                    }
                    setFormData((prev) => ({ ...prev, address: `${streetNumber} ${route}`, city: city, state: state, zip: postcode }));
                });
            } catch (error) { console.error("Maps Autocomplete Error:", error); }
        };

        if (!window.google || !window.google.maps) {
             if (!document.querySelector('#google-maps-script')) {
                const script = document.createElement('script');
                script.id = 'google-maps-script';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
                script.async = true;
                script.defer = true;
                script.onload = () => setTimeout(initAutocomplete, 500); 
                document.head.appendChild(script);
            }
        } else {
            setTimeout(initAutocomplete, 500);
        }
    }, []);

    const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
    const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };

    return (
        <form onSubmit={handleSubmit} className="p-4 space-y-4 bg-[#FFFBEB] h-full overflow-y-auto">
            <div className="bg-white p-4 rounded-xl border-2 border-black shadow-sm">
                <h3 className="font-['Bangers'] text-xl mb-4 flex items-center"><MapPin className="w-5 h-5 mr-2 text-orange-500"/> Shipping Details</h3>
                <div className="space-y-3">
                    <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">Full Name</label><input required type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" placeholder="Rubber Band Man" /></div>
                    <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">Email</label><input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" placeholder="you@example.com" /></div>
                    <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">Address</label><input id="shipping-address-input" required type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" placeholder="Start typing address..." autoComplete="street-address" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">City</label><input required type="text" name="city" value={formData.city} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" /></div>
                        <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">State</label><input required type="text" name="state" value={formData.state} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" /></div>
                    </div>
                    <div><label className="block text-xs font-bold text-gray-700 uppercase mb-1">Zip Code</label><input required type="text" name="zip" value={formData.zip} onChange={handleChange} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm focus:border-orange-500 focus:ring-0 outline-none" /></div>
                </div>
            </div>
            <button type="submit" className="w-full bg-black text-white px-6 py-3 rounded-lg font-bold text-lg border-2 border-black shadow-[3px_3px_0px_rgba(150,150,150,1)] hover:bg-orange-500 hover:text-black transition-colors active:translate-y-1 active:shadow-none">CONTINUE TO PAYMENT</button>
             <button type="button" onClick={onBack} className="w-full text-sm font-bold text-gray-500 hover:text-black underline">Back</button>
        </form>
    );
};

// --- Main Application ---

export default function RubberBandApp() {
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('cart'); 
  const [shippingInfo, setShippingInfo] = useState(null);
  const [orderId, setOrderId] = useState(null);
  
  // Subscription State - Default Colors: Rose, Emerald, Blue
  const [subColors, setSubColors] = useState(['#F43F5E', '#10B981', '#3B82F6']); 
  const [isSubCheckout, setIsSubCheckout] = useState(false);
  const [cancelEmail, setCancelEmail] = useState("");
  const [cancelMsg, setCancelMsg] = useState("");

  // Check for redirect return from Stripe (Cash App, Amazon Pay, etc.)
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const clientSecret = query.get('payment_intent_client_secret');

    if (clientSecret) {
      const savedOrderStr = localStorage.getItem('pendingOrder');
      if (savedOrderStr) {
        const savedOrder = JSON.parse(savedOrderStr);
        
        // Wait for Stripe to load before using it
        const checkStripe = setInterval(() => {
             if (window.Stripe) {
                 clearInterval(checkStripe);
                 const stripe = window.Stripe(STRIPE_PUBLIC_KEY);
                 stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
                     if (paymentIntent && paymentIntent.status === 'succeeded') {
                          handleSuccess(paymentIntent, savedOrder);
                     }
                 });
             }
        }, 100);
        
        // Fallback load if not already present
        if (!document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
             const script = document.createElement('script');
             script.src = 'https://js.stripe.com/v3/';
             script.async = true;
             document.body.appendChild(script);
        }
      }
    }
  }, []);

  const handleSuccess = (paymentIntent, savedOrder) => {
     const newOrderId = paymentIntent.id.slice(-6).toUpperCase();
     
     // Trigger Email
     fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            orderId: newOrderId,
            customerName: savedOrder.shippingInfo.name,
            customerEmail: savedOrder.shippingInfo.email,
            shippingAddress: savedOrder.shippingInfo,
            items: savedOrder.cart,
            total: savedOrder.total,
            isSubscription: savedOrder.isSubscription,
            subscriptionColors: savedOrder.subscriptionColors
        })
     }).catch(err => console.error("Email recovery failed", err));

     // Update UI
     setOrderId(newOrderId);
     setShippingInfo(savedOrder.shippingInfo); 
     setCart([]);
     setCartOpen(true);
     setCheckoutStep('success');
     
     // Clean up
     localStorage.removeItem('pendingOrder');
  };

  // Products List
  const products = [
    { id: 13, name: "Rainbow", price: 4.00, desc: "All The Colors!", c1: '#ff0000', c2: '#ff7f00', c3: '#ffff00', c4: '#00ff00', c5: '#0000ff', c6: '#8b00ff', pattern: 'hex', badge: "Number One Hustler" },
    { id: 10, name: "Crimson Tide", price: 2.50, desc: "Crimson & White Spirit", c1: '#990000', c2: '#ffffff', pattern: 'fishtail', badge: "Watch Me" },
    { id: 11, name: "Mint Forest", price: 3.00, desc: "Fresh Green Mix", c1: '#90ee90', c2: '#006400', c3: '#ffffff', pattern: 'hex', badge: "Grand Hustle" },
    { id: 12, name: "Christmas", price: 2.50, desc: "Festive Red & Green", c1: '#ff0000', c2: '#008000', pattern: 'fishtail', badge: "Fresh Drop" },
    { id: 7, name: "The Ice Pop", price: 3.00, desc: "Red, White & Blue Special", c1: "#ff0000", c2: "#0000ff", c3: "#ffffff", pattern: "fishtail", badge: "Number One Hustler" },
    { id: 1, name: "The Firestarter", price: 2.50, desc: "Red & Orange Fishtail", c1: "#ff4d4d", c2: "#ffcc00", pattern: "fishtail", badge: "Watch Me" },
    { id: 2, name: "Deep Sea Diver", price: 2.50, desc: "Blue & Teal Hexafish", c1: "#0066ff", c2: "#00ccff", pattern: "hex", new: false },
    { id: 3, name: "Toxic Slime", price: 2.50, desc: "Neon Green & Black", c1: "#ccff00", c2: "#000000", pattern: "fishtail", new: false },
    { id: 4, name: "Cotton Candy", price: 2.50, desc: "Pink & Blue Starburst", c1: "#ff99cc", c2: "#99ccff", pattern: "hex", new: true },
    { id: 5, name: "Midnight Stealth", price: 2.50, desc: "Black on Black", c1: "#333", c2: "#000", pattern: "fishtail", new: false },
    { id: 6, name: "Golden Glitch", price: 2.50, desc: "Gold Metallic Special", c1: "#ffd700", c2: "#DAA520", pattern: "hex", new: true },
    { id: 8, name: "Dragon Scale", price: 2.50, desc: "Green & Purple Mashup", c1: "#4ade80", c2: "#9333ea", pattern: "hex", new: false },
    { id: 9, name: "Berry Blast", price: 2.50, desc: "Purple & Pink Swirl", c1: "#d946ef", c2: "#8b5cf6", pattern: "fishtail", new: true },
  ];

  const addToCart = (productWithSize) => {
    setCart([...cart, { ...productWithSize, cartId: Math.random() }]);
    setCartOpen(true);
    setIsSubCheckout(false);
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const handleSubCheckout = () => {
      setCartOpen(true);
      setIsSubCheckout(true);
      setCheckoutStep('shipping');
  };

  const handleCancelSub = async (e) => {
      e.preventDefault();
      if(!cancelEmail) return;
      
      try {
          const res = await fetch(CANCEL_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: cancelEmail })
          });
          const data = await res.json();
          if(res.ok) {
              setCancelMsg("Success! Subscription cancelled.");
          } else {
              setCancelMsg(data.message || "Could not cancel. Email us?");
          }
      } catch(err) {
          setCancelMsg("Error connecting to server.");
      }
  };
  
  const handleCheckoutClick = () => {
    if (cart.length > 0) {
        setCheckoutStep('shipping');
    }
  };

  const handleShippingSubmit = (data) => {
      setShippingInfo(data);
      setCheckoutStep('payment');
  };

  const handlePaymentSuccess = (paymentIntent) => {
      const newOrderId = paymentIntent.id.slice(-6).toUpperCase();
      setOrderId(newOrderId);
      setCart([]); 
      setCheckoutStep('success'); 
  };

  const cartTotal = isSubCheckout ? "4.00" : cart.reduce((acc, item) => acc + item.price, 0).toFixed(2);

  return (
    <div className="min-h-screen bg-[#FFFBEB] font-sans selection:bg-orange-300">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bangers&family=Roboto:wght@400;700;900&display=swap');
      `}</style>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-orange-500 rounded-full border-2 border-black flex items-center justify-center animate-bounce">
                 <Star className="text-white w-6 h-6 fill-current" />
              </div>
              <span className="font-['Bangers'] text-3xl md:text-4xl tracking-wider pt-1">
                RUBBER BAND MAN
                <span className="text-orange-500 block text-sm font-sans font-bold -mt-2 tracking-normal leading-none md:inline md:ml-2 md:text-xl md:mt-0">CREATIONS</span>
              </span>
            </div>
            
            <div className="hidden md:flex space-x-8 font-black text-lg">
              <a href="#shop" className="hover:text-orange-500 transition">SHOP</a>
              <a href="#subscription" className="hover:text-orange-500 transition text-orange-600">JOIN THE CLUB</a>
              <a href="#custom" className="hover:text-orange-500 transition">CUSTOM</a>
              <a href="#about" className="hover:text-orange-500 transition">OUR STORY</a>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => { setCartOpen(true); setCheckoutStep('cart'); setIsSubCheckout(false); }}
                className="relative p-2 hover:bg-gray-100 rounded-full transition"
              >
                <ShoppingCart className="w-8 h-8 text-black" />
                {cart.length > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                    {cart.length}
                  </span>
                )}
              </button>
              <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)}>
                <Menu className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
        
        {menuOpen && (
          <div className="md:hidden bg-black text-white p-4 border-t-2 border-white">
            <a href="#shop" className="block py-2 font-bold text-xl" onClick={()=>setMenuOpen(false)}>SHOP</a>
            <a href="#subscription" className="block py-2 font-bold text-xl text-yellow-400" onClick={()=>setMenuOpen(false)}>JOIN THE CLUB</a>
            <a href="#custom" className="block py-2 font-bold text-xl" onClick={()=>setMenuOpen(false)}>CUSTOM</a>
            <a href="#about" className="block py-2 font-bold text-xl" onClick={()=>setMenuOpen(false)}>OUR STORY</a>
          </div>
        )}
      </nav>

      {/* Subscription Hero */}
      <div id="subscription" className="bg-black text-white py-16 border-b-4 border-white">
          <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 mb-8 md:mb-0">
                  <div className="inline-block bg-yellow-400 text-black font-black px-3 py-1 transform -rotate-2 mb-4 border-2 border-white shadow-[4px_4px_0px_rgba(255,255,255,1)]">
                      RUBBER BAND CLUB
                  </div>
                  <h2 className="font-['Bangers'] text-5xl md:text-6xl mb-4">GET 2 MYSTERY BANDS<br/>EVERY MONTH!</h2>
                  <p className="text-xl mb-6 font-bold text-gray-300">Only $4/Month. Cancel Anytime.</p>
                  
                  <div className="bg-gray-900 p-6 rounded-xl border-2 border-gray-700 mb-6">
                      <p className="font-bold mb-3 uppercase text-sm text-gray-400">Pick Your 3 Favorite Colors:</p>
                      <div className="flex space-x-4">
                          {subColors.map((c, i) => (
                              <input 
                                key={i} 
                                type="color" 
                                value={c} 
                                onChange={(e) => {
                                    const newColors = [...subColors];
                                    newColors[i] = e.target.value;
                                    setSubColors(newColors);
                                }}
                                className="w-12 h-12 rounded-full border-2 border-white cursor-pointer p-0 bg-transparent appearance-none"
                              />
                          ))}
                      </div>
                  </div>

                  <button onClick={handleSubCheckout} className="bg-orange-500 text-white px-8 py-4 rounded-xl font-black text-xl border-4 border-white shadow-[4px_4px_0px_rgba(255,255,255,1)] hover:translate-y-1 hover:shadow-none transition-all w-full md:w-auto">
                      SUBSCRIBE NOW - $4/MO
                  </button>
              </div>
              <div className="md:w-1/2 flex justify-center">
                  <div className="relative w-64 h-64 bg-white rounded-full border-4 border-white flex items-center justify-center animate-pulse">
                      <Heart className="w-32 h-32 text-red-500 fill-current" />
                  </div>
              </div>
          </div>
      </div>

      {/* Shop Section */}
      <div id="shop" className="max-w-7xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12 border-b-4 border-black pb-4">
            <div>
                <h2 className="font-['Bangers'] text-5xl text-black">FRESH DROPS</h2>
                <p className="font-bold text-gray-500">Grab 'em before they're gone!</p>
            </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} addToCart={addToCart} />
          ))}
        </div>
      </div>

       {/* About / "Our Story" Section */}
      <div id="about" className="bg-black text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-['Bangers'] text-5xl text-yellow-400 mb-6">WHO I BE... RUBBER BAND MAN</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-gray-900 p-8 rounded-2xl border-4 border-gray-700">
                <div className="order-2 md:order-1">
                    <p className="text-xl leading-relaxed mb-6 font-medium text-left">
                        "I started making bracelets and enjoyed it so much I wanted to make them for others. Now, I'm making them for everyone! 
                        Every single bracelet is hand-woven with high-quality bands that don't snap easily. 
                        Whether you want your team colors, neon vibes, or just something cool to wear, I got you."
                    </p>
                    <div className="flex justify-start items-center space-x-2">
                        <span className="font-['Bangers'] text-2xl text-orange-500">- The CEO (Age 7)</span>
                    </div>
                </div>
                <div className="order-1 md:order-2">
                    <div className="relative h-64 md:h-full w-full rounded-xl border-4 border-black overflow-hidden shadow-[4px_4px_0px_rgba(255,255,255,1)] transform -rotate-2 hover:rotate-0 transition-all">
                        <img src="image_a5acf9.png" alt="The Rubber Band Man CEO working" className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>
          </div>
      </div>

      {/* Custom Section & Cancel */}
      <div id="custom" className="bg-yellow-400 border-t-4 border-black py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
            <h2 className="font-['Bangers'] text-5xl mb-4">WANT A CUSTOM COLORWAY?</h2>
            <p className="text-xl font-bold mb-8 max-w-2xl mx-auto">We do custom orders for birthday parties, sports teams, and events! <span className="font-black text-black">Customs are just $4 each.</span> Email us your colors and we'll make it happen.</p>
            <a href="mailto:llcooldays2425@gmail.com" className="inline-block bg-black text-white font-bold text-lg px-8 py-3 rounded-full hover:bg-white hover:text-black border-4 border-black transition-colors mb-12">
                EMAIL US
            </a>

            <div className="max-w-md mx-auto bg-white p-6 rounded-xl border-4 border-black text-left">
                <h3 className="font-bold text-lg mb-2 flex items-center"><UserMinus className="w-5 h-5 mr-2"/> Manage Subscription</h3>
                <p className="text-sm text-gray-600 mb-4">Need to pause or cancel? Enter your email below.</p>
                <form onSubmit={handleCancelSub} className="flex gap-2">
                    <input 
                        type="email" 
                        placeholder="your@email.com" 
                        className="flex-1 border-2 border-gray-300 rounded p-2"
                        value={cancelEmail}
                        onChange={(e) => setCancelEmail(e.target.value)}
                        required
                    />
                    <button type="submit" className="bg-red-500 text-white font-bold px-4 rounded hover:bg-red-600">Cancel Plan</button>
                </form>
                {cancelMsg && <p className="text-sm font-bold mt-2 text-center">{cancelMsg}</p>}
            </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t-4 border-black py-12">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0 text-center md:text-left">
                <span className="font-['Bangers'] text-2xl block">RUBBER BAND MAN CREATIONS</span>
                <span className="text-sm font-bold text-gray-500">� 2025 All Rights Reserved.</span>
            </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-[60] overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setCartOpen(false)}></div>
          <div className="absolute inset-y-0 right-0 max-w-full flex">
            <div className="w-screen max-w-md bg-white border-l-4 border-black flex flex-col h-full shadow-2xl">
              
              {/* Cart Header */}
              <div className="px-4 py-6 bg-orange-500 border-b-4 border-black flex items-center justify-between">
                <h2 className="text-2xl font-['Bangers'] text-white tracking-wider">
                    {checkoutStep === 'success' ? 'SUCCESS!' : isSubCheckout ? 'JOIN THE CLUB' : 'YOUR STASH'}
                </h2>
                <button onClick={() => setCartOpen(false)} className="text-white hover:text-black">
                  <X className="w-8 h-8" />
                </button>
              </div>

              {/* Cart Content */}
              <div className="flex-1 overflow-y-auto bg-[#FFFBEB]">
                
                {/* STEP 1: CART ITEMS (Only show if NOT subscription checkout) */}
                {checkoutStep === 'cart' && !isSubCheckout && (
                    <div className="py-6 px-4">
                        {cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4 py-20">
                            <ShoppingCart className="w-16 h-16 opacity-20" />
                            <p className="font-bold text-xl text-black">Your stash is empty!</p>
                            <button onClick={() => setCartOpen(false)} className="text-orange-600 font-bold underline">Go add some bracelets</button>
                        </div>
                        ) : (
                        <ul className="space-y-4">
                            {cart.map((item) => (
                            <li key={item.cartId} className="flex py-4 bg-white p-4 rounded-xl border-2 border-black shadow-sm">
                                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                                <BraceletIcon color1={item.c1} color2={item.c2} color3={item.c3} color4={item.c4} color5={item.c5} color6={item.c6} pattern={item.pattern} />
                                </div>
                                <div className="ml-4 flex flex-1 flex-col">
                                    <div className="flex justify-between text-base font-bold text-gray-900">
                                        <h3>{item.name}</h3>
                                        <p className="ml-4">${item.price.toFixed(2)}</p>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">{item.desc}</p>
                                    <button onClick={() => removeFromCart(item.cartId)} className="text-red-600 text-sm font-bold mt-2 flex items-center"><Trash2 className="w-4 h-4 mr-1"/> Remove</button>
                                </div>
                            </li>
                            ))}
                        </ul>
                        )}
                    </div>
                )}

                {/* STEP 2: SHIPPING */}
                {checkoutStep === 'shipping' && (
                    <div className="h-full flex flex-col">
                        {isSubCheckout && (
                            <div className="p-4 bg-yellow-100 border-b-2 border-yellow-200">
                                <p className="font-bold text-orange-800 text-sm">Monthly Subscription: $4.00 / Month</p>
                                <p className="text-xs text-orange-700">Includes 2 mystery bracelets based on your colors.</p>
                            </div>
                        )}
                        <ShippingForm onSubmit={handleShippingSubmit} onBack={() => !isSubCheckout ? setCheckoutStep('cart') : setCartOpen(false)} initialData={shippingInfo} />
                    </div>
                )}

                {/* STEP 3: PAYMENT */}
                {checkoutStep === 'payment' && (
                    <div className="p-6 space-y-6">
                         <div className="border-t-4 border-black bg-white py-6 px-4 rounded-xl">
                            <div className="flex justify-between text-xl font-black text-gray-900 mb-4">
                                <p>{isSubCheckout ? 'Monthly Total' : 'Total'}</p>
                                <p>${cartTotal}</p>
                            </div>
                            <PaymentSection 
                                total={cartTotal} 
                                onPaymentSuccess={handlePaymentSuccess} 
                                shippingInfo={shippingInfo} 
                                cart={cart}
                                isSubscription={isSubCheckout}
                                subscriptionColors={subColors}
                            />
                        </div>
                    </div>
                )}

                {/* STEP 4: SUCCESS */}
                {checkoutStep === 'success' && (
                    <div className="p-8 text-center h-full flex flex-col items-center justify-center space-y-6">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-4 border-4 border-green-500 animate-bounce">
                            <CheckCircle className="w-12 h-12 text-green-600" />
                        </div>
                        <h2 className="font-['Bangers'] text-4xl text-gray-900">YOU'RE AWESOME!</h2>
                        <p className="text-lg font-bold text-gray-500">Order #{orderId}</p>
                        <p className="text-gray-600">Check your email for confirmation.</p>
                        <button onClick={() => { setCartOpen(false); setCheckoutStep('cart'); }} className="w-full bg-orange-500 text-white px-6 py-4 rounded-xl font-black text-xl border-4 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-1 transition-all mt-8">BACK TO SHOP</button>
                    </div>
                )}

              </div>

              {/* Cart Footer */}
              {checkoutStep === 'cart' && !isSubCheckout && cart.length > 0 && (
                <div className="border-t-4 border-black bg-white py-6 px-4">
                  <div className="flex justify-between text-xl font-black text-gray-900 mb-4">
                    <p>Subtotal</p>
                    <p>${cartTotal}</p>
                  </div>
                  <button onClick={handleCheckoutClick} className="w-full bg-black text-white px-6 py-3 rounded-lg font-bold text-lg border-2 border-black shadow-[3px_3px_0px_rgba(150,150,150,1)] hover:bg-orange-500 hover:text-black transition-colors active:translate-y-1 active:shadow-none">CHECKOUT</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}